import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge, dateBadge, sportName } from "./adminShared";
import { SportIcon } from "@/components/SportIcon";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Check, Star, Trash2, Pencil, X, ExternalLink, Loader2, MapPin, CalendarDays, Building2, Mail } from "lucide-react";

export default function AdminRaceReview() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.get(`/admin/races/${id}`).then(({ data }) => setData(data)).catch(() => toast.error("Not found")).finally(() => setLoading(false)); };
  useEffect(load, [id]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  if (loading) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#22d3ee]" size={28} /></div>;
  if (!data) return <p className="text-slate-500">Race not found.</p>;
  const r = data.race;
  const b = dateBadge(r.date);
  const h = r.highlights || {};

  return (
    <div>
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft size={16} /> Back</button>

      {/* Action bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-center gap-3 mb-6 sticky top-0 z-10">
        <div className="flex-1 min-w-[180px]">
          <div className="font-display font-black uppercase text-xl text-slate-800">{r.name}</div>
          <div className="flex items-center gap-2 mt-1"><StatusBadge status={r.status} /><span className="text-xs text-slate-400">Payment: {r.payment_status}</span></div>
        </div>
        <a href={`/race/${r.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 border border-slate-300 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-slate-100" data-testid="preview-as-runner"><ExternalLink size={15} /> Preview as Runner</a>
        <Link to={`/organizer/races/${r.id}/edit`} className="inline-flex items-center gap-1.5 border border-slate-300 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-slate-100"><Pencil size={15} /> Edit</Link>
        <button onClick={() => act(() => api.post(`/admin/races/${r.id}/feature?featured=${!r.featured}`), r.featured ? "Unfeatured" : "Featured")} className="p-2 border border-slate-300 rounded-lg hover:bg-amber-50" title="Feature"><Star size={16} className={r.featured ? "text-amber-500 fill-amber-500" : ""} /></button>
        <button onClick={() => act(() => api.post(`/admin/races/${r.id}/demo?is_demo=${!r.is_demo}`), r.is_demo ? "Unmarked demo" : "Marked as demo")} className={`px-3 py-2 rounded-lg text-sm font-bold border ${r.is_demo ? "bg-[#22d3ee] text-[#0a1420] border-[#22d3ee]" : "border-slate-300 text-slate-600"}`} data-testid="admin-demo-toggle" title="Mark as demo">{r.is_demo ? "Demo: ON" : "Demo: OFF"}</button>
        {r.status === "published" ? (
          <>
            <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-2 rounded-lg text-sm font-bold" data-testid="admin-published-indicator"><Check size={15} /> Published</span>
            <button onClick={() => act(() => api.post(`/admin/races/${r.id}/unpublish`), "Unpublished")} className="border border-slate-300 px-3 py-2 rounded-lg text-sm font-semibold" data-testid="admin-unpublish-button">Unpublish</button>
          </>
        ) : (
          <button onClick={() => act(() => api.post(`/admin/races/${r.id}/publish`), "Approved & Published")} className="inline-flex items-center gap-1.5 bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-emerald-700" data-testid="admin-approve-publish-button"><Check size={15} /> Approve &amp; Publish</button>
        )}
        {r.status !== "rejected" && r.status !== "published" && <RejectDialog onReject={(reason) => act(() => api.post(`/admin/races/${r.id}/reject`, { reason }), "Rejected")} />}
        <button onClick={() => { if (window.confirm("Delete permanently?")) act(() => api.delete(`/admin/races/${r.id}`).then(() => nav("/admin/races")), "Deleted"); }} className="p-2 border border-slate-300 rounded-lg hover:bg-red-50 hover:text-red-500"><Trash2 size={16} /></button>
      </div>

      {r.status === "rejected" && r.rejection_reason && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6 text-sm"><b>Rejection reason:</b> {r.rejection_reason}</div>}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {r.hero_image && <img src={r.hero_image} alt="" className="w-full h-56 object-cover rounded-xl" />}
          <Section title="Basic Information">
            <Row icon={SportIcon} sportSlug={r.sport} label="Sport" value={sportName(r.sport)} />
            <Row icon={CalendarDays} label="Date" value={`${b.day} ${b.month} ${b.year}`} />
            <Row icon={MapPin} label="Location" value={`${r.venue}, ${r.city}, ${r.state}, ${r.country}`} />
            <div className="pt-2"><p className="text-sm font-semibold text-slate-500">Description</p><p className="text-slate-700 mt-1 whitespace-pre-line">{r.description_full || r.description_short || "—"}</p></div>
          </Section>

          <Section title={`Categories (${(r.categories || []).length})`}>
            <table className="w-full text-sm"><thead className="text-slate-400 text-xs uppercase"><tr><th className="text-left pb-1">Name</th><th className="text-left pb-1">Distance</th><th className="text-right pb-1">Fee</th><th className="text-right pb-1">Start</th></tr></thead>
              <tbody>{(r.categories || []).map((c, i) => <tr key={i} className="border-t border-slate-100"><td className="py-2 font-semibold text-slate-800">{c.name}</td><td className="py-2 font-mono-data">{c.distance}</td><td className="py-2 text-right font-mono-data">RM{c.price}</td><td className="py-2 text-right">{c.start_time}</td></tr>)}</tbody>
            </table>
          </Section>

          {(r.schedule_items || []).length > 0 && (
            <Section title="Schedule">{r.schedule_items.map((s, i) => <div key={i} className="flex gap-4 py-1 text-sm"><span className="font-mono-data text-[#06b6d4] w-24">{s.time}</span><span className="text-slate-700">{s.label}</span></div>)}</Section>
          )}

          {(h.medal?.image_url || h.tshirt?.image_url || h.race_pack?.description) && (
            <Section title="Medal, T-Shirt & Merchandise">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {h.medal?.image_url && <MediaThumb img={h.medal.image_url} label="Finisher Medal" />}
                {h.tshirt?.image_url && <MediaThumb img={h.tshirt.image_url} label="Event T-Shirt" />}
                {(h.merchandise || []).filter((m) => m.image_url).map((m, i) => <MediaThumb key={i} img={m.image_url} label={m.name || "Merch"} />)}
              </div>
              {h.race_pack?.description && <p className="text-sm text-slate-600 mt-3"><b>Race Pack:</b> {h.race_pack.description}</p>}
            </Section>
          )}

          {(r.gallery || []).length > 0 && (
            <Section title={`Gallery (${r.gallery.length})`}><div className="grid grid-cols-3 sm:grid-cols-4 gap-2">{r.gallery.map((g, i) => <img key={i} src={g} alt="" className="w-full h-20 object-cover rounded-lg" />)}</div></Section>
          )}
        </div>

        <div className="space-y-6">
          <Section title="Organizer">
            <p className="font-semibold text-slate-800 flex items-center gap-2"><Building2 size={15} className="text-[#06b6d4]" />{r.organizer_name}</p>
            {data.organizer_contact && <p className="text-sm text-slate-600 flex items-center gap-2 mt-2"><Mail size={14} />{data.organizer_contact}</p>}
            {data.organizer?.phone && <p className="text-sm text-slate-600 mt-1">📞 {data.organizer.phone}</p>}
            {data.organizer?.website && <a href={data.organizer.website} target="_blank" rel="noreferrer" className="text-sm text-[#06b6d4] hover:underline mt-1 inline-block">{data.organizer.website}</a>}
          </Section>
          <Section title="Registration">
            <Row label="Status" value={r.registration_status?.replace(/_/g, " ")} />
            <Row label="Opens" value={r.registration_open_date || "—"} />
            <Row label="Deadline" value={r.registration_deadline || "—"} />
            {r.registration_url && <a href={r.registration_url} target="_blank" rel="noreferrer" className="text-sm text-[#06b6d4] hover:underline break-all">{r.registration_url}</a>}
          </Section>
          <Section title="Internal Metrics">
            <Row label="Views" value={r.views || 0} />
            <Row label="Saves" value={r.saves_count || 0} />
            <Row label="Shares" value={r.shares_count || 0} />
            <Row label="Register clicks" value={r.register_clicks || 0} />
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return <div className="bg-white border border-slate-200 rounded-xl p-5"><h3 className="font-display font-bold uppercase text-base text-slate-800 mb-3">{title}</h3><div className="space-y-1.5">{children}</div></div>;
}
function Row({ icon: Icon, sportSlug, label, value }) {
  return <div className="flex items-center gap-2 text-sm"><span className="text-slate-500 w-28 shrink-0 flex items-center gap-1.5">{sportSlug ? <SportIcon sport={sportSlug} size={14} className="text-[#06b6d4]" /> : Icon ? <Icon size={14} className="text-[#06b6d4]" /> : null}{label}</span><span className="text-slate-800 font-medium">{value}</span></div>;
}
function MediaThumb({ img, label }) {
  return <div><img src={img} alt={label} className="w-full h-24 object-cover rounded-lg" /><p className="text-xs text-slate-500 mt-1">{label}</p></div>;
}
function RejectDialog({ onReject }) {
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="inline-flex items-center gap-1.5 border border-red-200 text-red-500 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-red-50" data-testid="admin-reject-button"><X size={15} /> Reject</button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Reject Race</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">The organizer will see this reason and can edit &amp; resubmit.</p>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Reason for rejection..." className="w-full border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-[#22d3ee]" data-testid="reject-reason-input" />
        <DialogFooter>
          <button onClick={() => setOpen(false)} className="border border-slate-300 px-4 py-2 rounded-lg font-semibold">Cancel</button>
          <button onClick={() => { if (!reason.trim()) return; onReject(reason); setOpen(false); }} className="bg-red-500 text-white px-4 py-2 rounded-lg font-semibold" data-testid="confirm-reject-button">Confirm Reject</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
